/**
 * Zapier Integration — bidirectional
 *
 * OUTBOUND (Velaro → Zapier): Configure a Catch Hook URL; workflow nodes POST conversation data.
 * INBOUND  (Zapier → Velaro): API key lets Zapier enrich contacts, inject messages, update fields.
 * REST HOOKS: Zapier subscribes to Velaro events (conversation.started/resolved, etc.)
 */
import React, { useCallback, useEffect, useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import StarterWorkflowWizard from "../StarterWorkflowWizard";

interface Config {
  configured: boolean;
  displayName: string;
  isEnabled: boolean;
  webhookUrlMasked: string;
}

interface ApiKeyStatus {
  hasKey: boolean;
  hint?: string;
}

function ZapierLogo() {
  return (
    <svg viewBox="0 0 100 100" className="w-10 h-10" fill="none">
      <rect width="100" height="100" rx="18" fill="#FF4A00" />
      <path d="M50 20 L62 44 L88 44 L68 60 L76 84 L50 68 L24 84 L32 60 L12 44 L38 44 Z" fill="white" />
    </svg>
  );
}

const TRIGGERS = [
  { event: "conversation.started",  label: "Conversation Started",  desc: "Fires when a visitor starts a chat. Payload includes contact info, team, source." },
  { event: "conversation.resolved", label: "Conversation Resolved", desc: "Fires when an agent or bot resolves a conversation. Includes transcript URL." },
  { event: "contact.created",       label: "Contact Created",       desc: "Fires when Velaro creates a new contact record." },
  { event: "bot.escalated",         label: "Bot Escalated",         desc: "Fires when a bot hands off to a human agent." },
];

const ACTIONS = [
  { name: "enrich-contact",  label: "Enrich Contact",         desc: "Push contact fields (name, custom data) into Velaro before or after chat." },
  { name: "inject-event",    label: "Inject Message",         desc: "Send a real-time message into an active conversation (e.g., 'Your order shipped!')." },
  { name: "update-contact",  label: "Update Contact Fields",  desc: "Update any contact field or custom attribute from an external event." },
];

export default function ZapierPage() {
  const api = useApi();
  const [activeTab, setActiveTab]       = useState<"setup" | "inbound" | "reference">("setup");
  const [config, setConfig]             = useState<Config | null>(null);
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus | null>(null);
  const [newApiKey, setNewApiKey]       = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl]     = useState("");
  const [displayName, setDisplayName]   = useState("");
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [error, setError]               = useState("");
  const [showWizard, setShowWizard]     = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const [cfgRes, keyRes] = await Promise.all([
        api.messaging.get("Zapier/config"),
        api.messaging.get("Zapier/apikey/status"),
      ]);
      const cfg = cfgRes.ok ? await cfgRes.json() : null;
      const key = keyRes.ok ? await keyRes.json() : null;
      setConfig(cfg ?? { configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" });
      setApiKeyStatus(key ?? { hasKey: false });
      if (cfg?.displayName) setDisplayName(cfg.displayName);
    } catch {
      setConfig({ configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" });
      setApiKeyStatus({ hasKey: false });
    }
  }, [api.messaging]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  async function saveOutbound() {
    if (!webhookUrl.trim()) { setError("Webhook URL is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await api.messaging.post("Zapier/config", { webhookUrl, displayName, isEnabled: true });
      if (res.ok) {
        setSaved(true);
        await fetchConfig();
        setWebhookUrl("");
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save. Check the webhook URL and try again.");
      }
    } catch { setError("Network error."); }
    finally { setSaving(false); }
  }

  async function generateApiKey() {
    setGeneratingKey(true); setNewApiKey(null);
    try {
      const res = await api.messaging.post("Zapier/apikey/generate", {});
      if (res.ok) {
        const data = await res.json();
        setNewApiKey(data.apiKey);
        await fetchConfig();
      }
    } catch { /* swallow */ }
    finally { setGeneratingKey(false); }
  }

  const isConnected = config?.configured || apiKeyStatus?.hasKey;

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard integration="zapier" onClose={() => setShowWizard(false)} />
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 border border-slate-300 rounded p-2 flex items-center justify-center">
          <ZapierLogo />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-slate-800 text-lg">Zapier</div>
          <div className="text-slate-400 text-sm">
            Bidirectional automation with 7,000+ apps. Trigger Zaps from Velaro events, or let Zapier update contacts and inject messages in real time.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Connected
            </span>
          )}
          {isConnected && (
            <SecondaryBlueButton label="Launch Starter Wizard" onClick={() => setShowWizard(true)} prefixIcon={null} />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6 gap-1">
        {([
          { id: "setup",     label: "Outbound (Velaro → Zapier)" },
          { id: "inbound",   label: "Inbound (Zapier → Velaro)" },
          { id: "reference", label: "Payload Reference" },
        ] as const).map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? "border-orange-500 text-orange-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Outbound ── */}
      {activeTab === "setup" && (
        <div className="space-y-5">
          {/* Event triggers reference */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-3">Available Triggers</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TRIGGERS.map(t => (
                <div key={t.event} className="flex gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50">
                  <span className="w-2 h-2 mt-1.5 rounded-full bg-orange-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-slate-700">{t.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                    <code className="text-[10px] text-orange-600 font-mono">{t.event}</code>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Triggers fire automatically via REST Hooks when you connect Zapier using your API key (Inbound tab).
              You can also trigger Zaps manually from any workflow node.
            </p>
          </div>

          {/* Outbound Catch Hook setup */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-1">Workflow Node — Catch Hook URL</div>
            <p className="text-xs text-slate-500 mb-4">
              Add a <strong>Zapier</strong> node to any workflow to POST conversation data to a Zapier Catch Hook.
              Paste the Catch Hook URL from Zapier below.
            </p>

            {config?.configured && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-100 text-sm">
                <span className="font-medium text-green-700">{config.displayName || "Active Catch Hook"}</span>
                <span className="ml-2 text-green-500 font-mono text-xs">{config.webhookUrlMasked}</span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Zap Name (optional)</label>
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder="e.g. New Lead to Salesforce"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Zapier Catch Hook URL *</label>
                <input
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  value={webhookUrl}
                  onChange={e => { setWebhookUrl(e.target.value); setError(""); }}
                />
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
              {saved && <p className="text-xs text-green-600">Saved successfully.</p>}
            </div>
            <div className="mt-4">
              <PrimaryBlueButton label={saving ? "Saving..." : "Save Catch Hook"} onClick={saveOutbound} prefixIcon={null} />
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Inbound ── */}
      {activeTab === "inbound" && (
        <div className="space-y-5">
          {/* API Key */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-1">API Key</div>
            <p className="text-xs text-slate-500 mb-4">
              Zapier uses this key to authenticate calls to your Velaro endpoints.
              Enter it as the <code className="bg-slate-100 px-1 rounded font-mono text-xs">X-Velaro-Api-Key</code> header in your Zap's action.
            </p>

            {apiKeyStatus?.hasKey && !newApiKey && (
              <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100 text-sm flex items-center gap-2">
                <span className="text-green-600 font-medium">API key configured</span>
                <span className="text-slate-400 font-mono text-xs">{apiKeyStatus.hint}</span>
              </div>
            )}

            {newApiKey && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-xs font-semibold text-amber-800 mb-1">Copy this key now — it won't be shown again.</p>
                <code className="block font-mono text-sm text-amber-900 break-all">{newApiKey}</code>
              </div>
            )}

            <SecondaryBlueButton
              label={generatingKey ? "Generating..." : apiKeyStatus?.hasKey ? "Regenerate API Key" : "Generate API Key"}
              onClick={generateApiKey}
              prefixIcon={null}
            />
          </div>

          {/* Available Actions */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-3">What Zapier Can Do to Velaro</div>
            <div className="space-y-3">
              {ACTIONS.map(a => (
                <div key={a.name} className="flex gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50">
                  <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-400 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-700">{a.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Endpoint reference */}
            <div className="mt-4 rounded-lg bg-slate-900 text-slate-100 p-4 text-xs font-mono overflow-auto">
              <div className="text-slate-400 mb-2"># Enrich contact before/after chat</div>
              <div>POST https://app.velaro.com/api/public/zapier/<span className="text-orange-400">{"{siteId}"}</span>/enrich-contact</div>
              <div>X-Velaro-Api-Key: <span className="text-green-400">your-api-key</span></div>
              <div className="mt-2 text-slate-400">{"{ \"email\": \"...\", \"phone\": \"...\", \"customFields\": { \"tier\": \"Enterprise\" } }"}</div>
              <div className="mt-4 text-slate-400"># Inject a message into an active conversation</div>
              <div>POST https://app.velaro.com/api/public/zapier/<span className="text-orange-400">{"{siteId}"}</span>/inject-event</div>
              <div>X-Velaro-Api-Key: <span className="text-green-400">your-api-key</span></div>
              <div className="mt-2 text-slate-400">{"{ \"contactEmail\": \"...\", \"message\": \"Your order shipped!\", \"senderName\": \"Order Bot\" }"}</div>
            </div>
          </div>

          {/* REST Hook reference */}
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-1">REST Hook Subscriptions</div>
            <p className="text-xs text-slate-500 mb-3">
              The official Velaro Zapier app uses REST Hooks to subscribe to Velaro events.
              When you turn on a Zap trigger, Zapier automatically registers and deregisters its URL.
            </p>
            <div className="rounded-lg bg-slate-900 text-slate-100 p-4 text-xs font-mono overflow-auto">
              <div className="text-slate-400"># Subscribe to an event</div>
              <div>POST /api/public/zapier/<span className="text-orange-400">{"{siteId}"}</span>/hooks/subscribe</div>
              <div className="mt-2 text-slate-400">{"{ \"event\": \"conversation.resolved\", \"targetUrl\": \"https://hooks.zapier.com/...\" }"}</div>
              <div className="mt-4 text-slate-400"># Unsubscribe</div>
              <div>DELETE /api/public/zapier/<span className="text-orange-400">{"{siteId}"}</span>/hooks/<span className="text-orange-400">{"{subscriptionId}"}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Payload Reference ── */}
      {activeTab === "reference" && (
        <div className="space-y-5">
          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-3">Outbound payload (Velaro → Zapier)</div>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "event": "conversation.resolved",
  "conversationId": 98765,
  "siteId": 1001,
  "contactId": 42,
  "contactEmail": "visitor@example.com",
  "contactName": "John Smith",
  "contactPhone": "+15551234567",
  "teamId": 7,
  "status": "resolved",
  "source": "web",
  "startedAt": "2026-03-10T14:30:00Z",
  "resolvedAt": "2026-03-10T14:48:00Z",
  "timestamp": "2026-03-10T14:48:01Z"
}`}</pre>
            <p className="text-xs text-slate-500 mt-2">
              Workflow nodes also send <code className="font-mono bg-slate-100 px-1 rounded">workflowVariables</code> with any
              variables collected during the conversation (survey answers, CRM lookups, slot values).
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 p-5">
            <div className="font-medium text-slate-800 mb-3">Inbound payloads (Zapier → Velaro)</div>

            <div className="text-xs font-semibold text-slate-600 mb-1 mt-2">Enrich/Update Contact</div>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "email": "visitor@example.com",
  "phone": "+15551234567",
  "name": "John Smith",
  "customFields": {
    "salesforce_account_id": "0011234",
    "tier": "Enterprise",
    "arr": "240000"
  }
}`}</pre>

            <div className="text-xs font-semibold text-slate-600 mb-1 mt-4">Inject Message into Active Conversation</div>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "contactEmail": "visitor@example.com",
  "message": "Great news — your order #ORD-1234 just shipped! Tracking: UPS 1Z999AA10123456784",
  "senderName": "Order Bot"
}`}</pre>
          </div>

          <div className="rounded-lg border border-blue-50 bg-blue-50 p-5">
            <div className="font-semibold text-blue-800 mb-2">Example: order shipped → inject message</div>
            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
              <li>Shopify fires "Order Fulfilled" → Zap catches it</li>
              <li>Zap maps order.email + tracking number</li>
              <li>Zap action: POST to Velaro inject-event with contact email + message</li>
              <li>If customer has an open conversation, the message appears instantly</li>
              <li>Agent sees it in real time — no tab-switching, no manual lookup</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
